import { computed, type Ref } from "vue";
import { useHead } from "#imports";
import { isRegion } from "@@/composables/helpers";
import { JobItem } from "../useJob";
import { isApplicationDeadlineUnlimited } from "@@/utils/jobDeadline";

export const useJobSchema = (jobItem: Ref<JobItem | null>, locale: Ref<string>) => {
	const jsonLdSchema = computed(() => {
		// If the job hasn't loaded yet, return null
		if (!jobItem.value) return null;

		const job = jobItem.value;
		const currentLocale = locale.value;

		let fullDescription = job?.boxes?.job_job_description?.data?.description?.[currentLocale] || "";
		const tasks = job?.boxes?.job_tasks?.data?.[currentLocale] || [];

		if (tasks.length > 0) {
			fullDescription += "<h3>Responsibilities:</h3><ul>";
			tasks.forEach((task: string) => {
				fullDescription += `<li>${task}</li>`;
			});
			fullDescription += "</ul>";
		}

		// Fallback to company mission if job description is empty
		if (!fullDescription) {
			fullDescription = job?.company?.boxes?.mission?.data?.mission?.[currentLocale] || job.name[currentLocale];
		}

		// Standardize Employment Type (Google strictly expects FULL_TIME, PART_TIME, INTERN, etc.)
		const employmentTypes = [];
		const typeSlug = job?.tags?.job_type?.data?.[0]?.slug;
		if (typeSlug === "full-time") employmentTypes.push("FULL_TIME");
		if (typeSlug === "part-time") employmentTypes.push("PART_TIME");
		if (job?.tags?.job_level?.slug === "intern") employmentTypes.push("INTERN");
		if (employmentTypes.length === 0) employmentTypes.push("FULL_TIME"); // Fallback

		// Extract Skills as a comma-separated string
		const skillsArray = job?.tags?.skill?.data?.map((s: unknown) => s.name[currentLocale] || s.name["en"]) || [];
		const softSkillsArray =
			job?.tags?.soft_skill?.data?.map((s: unknown) => s.name[currentLocale] || s.name["en"]) || [];
		const allSkills = [...skillsArray, ...softSkillsArray].join(", ");

		// Extract Benefits
		const benefitsArray = [
			...(job?.tags?.benefits_catering?.data || []),
			...(job?.tags?.benefits_wellbeing?.data || []),
			...(job?.tags?.benefits_assets?.data || []),
		].map((b: unknown) => b.name[currentLocale] || b.name["en"]);

		const datePosted = new Date(job.created_at || Date.now());

		const isRemote = job.badges?.some((b: { slug: string }) => b.slug === "tavmunka") ?? false;
		const hasRealOffice = (job.job_offices?.length ?? 0) > 0 && !!job.job_offices?.[0]?.formatted_address;

		const schema: Record<string, unknown> = {
			"@context": "https://schema.org/",
			"@type": "JobPosting",
			title: job.name[currentLocale],
			description: fullDescription,
			datePosted: datePosted.toISOString(),
			...(job.expires_at && !isApplicationDeadlineUnlimited(job) ? { validThrough: job.expires_at } : {}),
			employmentType: employmentTypes,

			industry: job?.tags?.job_category?.name?.[currentLocale] || undefined,
			skills: allSkills.length > 0 ? allSkills : undefined,
			jobBenefits: benefitsArray.length > 0 ? benefitsArray.join(", ") : undefined,
			workHours: job?.boxes?.job_work_schedule?.data?.weekly_hours
				? `${job.boxes.job_work_schedule.data.weekly_hours} hours per week`
				: undefined,

			hiringOrganization: {
				"@type": "Organization",
				name: job.company?.name || job.company?.slug || "Confidential", // added fallback for name
				sameAs: job.company?.follow_us?.web_address || undefined,
				...(job.company?.logo?.image && { logo: job.company.logo.image }),
			},
		};

		if (hasRealOffice) {
			schema.jobLocation = {
				"@type": "Place",
				address: {
					"@type": "PostalAddress",
					streetAddress: job.job_offices?.[0]?.formatted_address || "",
					addressLocality: job.job_offices?.[0]?.locality || "",
					addressRegion: job.job_offices?.[0]?.country_code || "",
					postalCode: job.job_offices?.[0]?.postal_code || "",
					addressCountry: job.job_offices?.[0]?.country || (isRegion("hu") ? "HU" : "RO"),
				},
			};
			if (isRemote) {
				schema.jobLocationType = "TELECOMMUTE";
				schema.applicantLocationRequirements = {
					"@type": "Country",
					name: isRegion("hu") ? "HU" : "RO",
				};
			}
		} else {
			schema.jobLocationType = "TELECOMMUTE";
			schema.applicantLocationRequirements = {
				"@type": "Country",
				name: isRegion("hu") ? "HU" : "RO",
			};
		}

		// Handle Salary Properly (Parsing the "3333 - 7777" string)
		if (job?.jobSalary?.notSpecified === false && job.jobSalary.salary) {
			const currency = (job.jobSalary.currency || "HUF").toUpperCase();

			// Check if it's a range (e.g., "3333 - 7777")
			if (job.jobSalary.salary.includes("-")) {
				const parts = job.jobSalary.salary
					.replace(/\s+/g, "")
					.split("-")
					.map((p) => parseFloat(p.trim()));
				schema.baseSalary = {
					"@type": "MonetaryAmount",
					currency: currency,
					value: {
						"@type": "QuantitativeValue",
						minValue: parts[0],
						maxValue: parts[1],
						unitText: "MONTH", // Update if amount_in defines otherwise
					},
				};
			} else {
				// Single value
				schema.baseSalary = {
					"@type": "MonetaryAmount",
					currency: currency,
					value: {
						"@type": "QuantitativeValue",
						value: parseFloat(job.jobSalary.salary),
						unitText: "MONTH",
					},
				};
			}
		}

		// Clean up undefined properties
		Object.keys(schema).forEach((key) => schema[key] === undefined && delete schema[key]);

		return schema;
	});

	// Inject into the <head>
	useHead(() => ({
		script: [
			{
				type: "application/ld+json",
				innerHTML: jsonLdSchema.value ? JSON.stringify(jsonLdSchema.value) : undefined,
			},
		],
	}));
};
